import apiClient from '../api';

// Type definitions matching backend DTOs
export interface CreateCertificationDto {
  certification_name: string;
  description?: string;
  targeted_audience?: string;
  required_course_ids: string[];
  exam_config: {
    total_questions: number;
    question_types?: string[];
    difficulty_distribution?: {
      easy_percentage: number;
      medium_percentage: number;
      hard_percentage: number;
    };
    time_limit_minutes?: number;
    passing_score: number;
    shuffle_questions?: boolean;
    shuffle_options?: boolean;
    show_feedback?: boolean;
    show_correct_answers?: boolean;
    allow_retry?: boolean;
    max_attempts?: number;
  };
  validity_type?: string;
  validity_value?: number;
}

export interface UpdateCertificationDto {
  certification_name?: string;
  description?: string;
  targeted_audience?: string;
  required_course_ids?: string[];
  exam_config?: Partial<CreateCertificationDto['exam_config']>;
  validity_type?: string;
  validity_value?: number;
}

export interface CertificationDto {
  _id: string;
  certification_name: string;
  description?: string;
  targeted_audience?: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  required_course_ids: string[];
  exam_config: CreateCertificationDto['exam_config'];
  certification_exam_id?: string;
  created_by: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface CertificationFilterDto {
  status?: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  targeted_audience?: string;
  search?: string;
  limit?: number;
  skip?: number;
}

export interface CertificationExamDto {
  _id: string;
  certification_id: string;
  title: string;
  description?: string;
  questions: any[];
  total_points: number;
  passing_score: number;
  time_limit_minutes?: number;
  max_attempts?: number;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  is_published: boolean;
  generated_by: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface UpdateExamQuestionsDto {
  questions: any[];
}

export interface UpdateExamSettingsDto {
  time_limit_minutes?: number;
  passing_score?: number;
  max_attempts?: number;
  shuffle_questions?: boolean;
  shuffle_options?: boolean;
  show_feedback?: boolean;
  show_correct_answers?: boolean;
  allow_retry?: boolean;
}

class CertificationApiService {
  private baseUrl = '/certifications';
  private examBaseUrl = '/certification-exams';

  // ==================== Certification Management ====================

  async listCertifications(
    filters: CertificationFilterDto = {}
  ): Promise<{ data: CertificationDto[]; total: number }> {
    const response = await apiClient.post(`${this.baseUrl}/utils/list`, filters);
    return response.data.data;
  }

  async getCertificationById(id: string): Promise<CertificationDto> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  async createCertification(
    dto: CreateCertificationDto
  ): Promise<CertificationDto> {
    const response = await apiClient.post(this.baseUrl, dto);
    return response.data.data;
  }

  async updateCertification(
    id: string,
    dto: UpdateCertificationDto
  ): Promise<CertificationDto> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, dto);
    return response.data.data;
  }

  async deleteCertification(id: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  async publishCertification(id: string): Promise<CertificationDto> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/publish`);
    return response.data.data;
  }

  async archiveCertification(id: string): Promise<CertificationDto> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/archive`);
    return response.data.data;
  }

  // ==================== Exam Management ====================

  async getExamById(examId: string): Promise<CertificationExamDto> {
    const response = await apiClient.get(`${this.examBaseUrl}/${examId}`);
    return response.data.data;
  }

  async getExamByCertificationId(
    certificationId: string
  ): Promise<CertificationExamDto> {
    const response = await apiClient.get(
      `${this.examBaseUrl}/certification/${certificationId}`
    );
    return response.data.data;
  }

  async updateExamQuestions(
    examId: string,
    dto: UpdateExamQuestionsDto
  ): Promise<CertificationExamDto> {
    const response = await apiClient.put(
      `${this.examBaseUrl}/${examId}/questions`,
      dto
    );
    return response.data.data;
  }

  async addQuestion(
    examId: string,
    question: any
  ): Promise<CertificationExamDto> {
    const response = await apiClient.post(
      `${this.examBaseUrl}/${examId}/questions`,
      question
    );
    return response.data.data;
  }

  async removeQuestion(
    examId: string,
    questionId: string
  ): Promise<CertificationExamDto> {
    const response = await apiClient.delete(
      `${this.examBaseUrl}/${examId}/questions/${questionId}`
    );
    return response.data.data;
  }

  async publishExam(examId: string): Promise<CertificationExamDto> {
    const response = await apiClient.post(
      `${this.examBaseUrl}/${examId}/publish`
    );
    return response.data.data;
  }

  async updateExamSettings(
    examId: string,
    dto: UpdateExamSettingsDto
  ): Promise<CertificationExamDto> {
    const response = await apiClient.put(
      `${this.examBaseUrl}/${examId}/settings`,
      dto
    );
    return response.data.data;
  }
}

export const certificationApiService = new CertificationApiService();
